import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'
import { zustandPlugin } from '@/modules/shared/openreplay'

type State = {
  isOpenEditDriverModal: boolean
  isCreateContractOpen: boolean
  isCreateAnnexOpen: boolean
  isPrintActionsOpen: boolean
  isAddActionsOpen: boolean
  isDeactivateOpen: boolean
  showSearch: boolean
  isAnnexOpen: boolean
  isOpenEditContractModal: boolean
  isEditAnnexOpen: boolean
  isOpenCreateTemplate: boolean
  isOpenContractFromTemplate: boolean
  isOpenContractWithDataFromTemplate: boolean
  selectedTemplateId: string | null
  selectedContractIdFromTemplate: string | null
}

type Setter<T> = React.Dispatch<React.SetStateAction<T>>

type Actions = {
  setIsOpenEditDriverModal: Setter<boolean>
  setIsCreateContractOpen: Setter<boolean>
  setIsCreateAnnexOpen: Setter<boolean>
  setIsPrintActionsOpen: Setter<boolean>
  setIsAddActionsOpen: Setter<boolean>
  setIsDeactivateOpen: Setter<boolean>
  setShowSearch: Setter<boolean>
  setIsAnnexOpen: Setter<boolean>
  setIsOpenEditContractModal: Setter<boolean>
  setIsEditAnnexOpen: Setter<boolean>
  setIsOpenCreateTemplate: Setter<boolean>
  setIsOpenContractFromTemplate: Setter<boolean>
  setIsOpenContractWithDataFromTemplate: Setter<boolean>
  setSelectedTemplateId: Setter<string | null>
  setSelectedContractIdFromTemplate: Setter<string | null>
  reset: () => void
}

const initialState: State = {
  isOpenEditDriverModal: false,
  isCreateContractOpen: false,
  isCreateAnnexOpen: false,
  isPrintActionsOpen: false,
  isAddActionsOpen: false,
  isDeactivateOpen: false,
  showSearch: false,
  isAnnexOpen: false,
  isOpenEditContractModal: false,
  isEditAnnexOpen: false,
  isOpenCreateTemplate: false,
  isOpenContractFromTemplate: false,
  isOpenContractWithDataFromTemplate: false,
  selectedTemplateId: null,
  selectedContractIdFromTemplate: null,
}

const makeSetter = <K extends keyof State>(
  set: (partial: Partial<State>, replace?: boolean, action?: string) => void,
  get: () => State,
  key: K,
  actionName: string,
): Setter<State[K]> => {
  return (updater) => {
    const prev = get()[key]
    const next =
      typeof updater === 'function'
        ? (updater as (p: State[K]) => State[K])(prev)
        : updater
    set({ [key]: next } as Partial<State>, false, actionName)
  }
}

export const useDriversModalsStore = create<State & Actions>()(
  devtools(
    zustandPlugin((set, get) => ({
      ...initialState,
      setIsOpenEditDriverModal: makeSetter(
        set,
        get,
        'isOpenEditDriverModal',
        'setIsOpenEditDriverModal',
      ),
      setIsCreateContractOpen: makeSetter(
        set,
        get,
        'isCreateContractOpen',
        'setIsCreateContractOpen',
      ),
      setIsCreateAnnexOpen: makeSetter(
        set,
        get,
        'isCreateAnnexOpen',
        'setIsCreateAnnexOpen',
      ),
      setIsPrintActionsOpen: makeSetter(
        set,
        get,
        'isPrintActionsOpen',
        'setIsPrintActionsOpen',
      ),
      setIsAddActionsOpen: makeSetter(
        set,
        get,
        'isAddActionsOpen',
        'setIsAddActionsOpen',
      ),
      setIsDeactivateOpen: makeSetter(
        set,
        get,
        'isDeactivateOpen',
        'setIsDeactivateOpen',
      ),
      setShowSearch: makeSetter(set, get, 'showSearch', 'setShowSearch'),
      setIsAnnexOpen: makeSetter(
        set,
        get,
        'isAnnexOpen',
        'setIsAnnexOpen',
      ),
      setIsOpenEditContractModal: makeSetter(
        set,
        get,
        'isOpenEditContractModal',
        'setIsOpenEditContractModal',
      ),
      setIsEditAnnexOpen: makeSetter(
        set,
        get,
        'isEditAnnexOpen',
        'setIsEditAnnexOpen',
      ),
      setIsOpenCreateTemplate: makeSetter(
        set,
        get,
        'isOpenCreateTemplate',
        'setIsOpenCreateTemplate',
      ),
      setIsOpenContractFromTemplate: makeSetter(
        set,
        get,
        'isOpenContractFromTemplate',
        'setIsOpenContractFromTemplate',
      ),
      setIsOpenContractWithDataFromTemplate: makeSetter(
        set,
        get,
        'isOpenContractWithDataFromTemplate',
        'setIsOpenContractWithDataFromTemplate',
      ),
      setSelectedTemplateId: makeSetter(
        set,
        get,
        'selectedTemplateId',
        'setSelectedTemplateId',
      ),
      setSelectedContractIdFromTemplate: makeSetter(
        set,
        get,
        'selectedContractIdFromTemplate',
        'setSelectedContractIdFromTemplate',
      ),
      reset: () => set(initialState, false, 'reset'),
    }), 'drivers-modals'),
    { name: 'EasyTacho', store: 'drivers-modals' },
  ),
)

export const useModals = () =>
  useDriversModalsStore(
    useShallow((s) => ({
      isOpenEditDriverModal: s.isOpenEditDriverModal,
      setIsOpenEditDriverModal: s.setIsOpenEditDriverModal,
      isCreateContractOpen: s.isCreateContractOpen,
      setIsCreateContractOpen: s.setIsCreateContractOpen,
      isCreateAnnexOpen: s.isCreateAnnexOpen,
      setIsCreateAnnexOpen: s.setIsCreateAnnexOpen,
      isPrintActionsOpen: s.isPrintActionsOpen,
      setIsPrintActionsOpen: s.setIsPrintActionsOpen,
      isAddActionsOpen: s.isAddActionsOpen,
      setIsAddActionsOpen: s.setIsAddActionsOpen,
      isDeactivateOpen: s.isDeactivateOpen,
      setIsDeactivateOpen: s.setIsDeactivateOpen,
      showSearch: s.showSearch,
      setShowSearch: s.setShowSearch,
      isAnnexOpen: s.isAnnexOpen,
      setIsAnnexOpen: s.setIsAnnexOpen,
      isOpenEditContractModal: s.isOpenEditContractModal,
      setIsOpenEditContractModal: s.setIsOpenEditContractModal,
      isEditAnnexOpen: s.isEditAnnexOpen,
      setIsEditAnnexOpen: s.setIsEditAnnexOpen,
      isOpenCreateTemplate: s.isOpenCreateTemplate,
      setIsOpenCreateTemplate: s.setIsOpenCreateTemplate,
      isOpenContractFromTemplate: s.isOpenContractFromTemplate,
      setIsOpenContractFromTemplate: s.setIsOpenContractFromTemplate,
      isOpenContractWithDataFromTemplate: s.isOpenContractWithDataFromTemplate,
      setIsOpenContractWithDataFromTemplate: s.setIsOpenContractWithDataFromTemplate,
      selectedTemplateId: s.selectedTemplateId,
      setSelectedTemplateId: s.setSelectedTemplateId,
      selectedContractIdFromTemplate: s.selectedContractIdFromTemplate,
      setSelectedContractIdFromTemplate: s.setSelectedContractIdFromTemplate,
    })),
  )
